import React from 'react';
import { usePuck } from '@puckeditor/core';
import type { ComponentConfig, Fields } from '@puckeditor/core';
import { sanitizeUrl } from '@braintree/sanitize-url';
import styled, { css } from 'styled-components';

import { mediaQueries } from 'src/theme/constants/media';

import { FieldLabelWithHelp } from '../fields/widgets/field-label-with-help';
import { ImageSourceField } from '../fields/widgets/image-source-field';
import { focalPointField } from '../fields/widgets/focal-point-field';
import { altTextField, resolveAltText } from '../fields/widgets/alt-text-field';
import type { AltTextValue } from '../fields/widgets/alt-text-field';
import { selectField } from '../fields/widgets/select-field';
import { destinationField } from '../fields/widgets/destination-field';
import { toggleField } from '../fields/widgets/toggle-field';
import { createFlatSpacingFields, readBoxSpacing } from '../fields/spacing-fields';
import type { FlatSpacingProps } from '../fields/spacing-fields';
import { renderFieldGroups } from '../fields/field-groups';
import type { FieldGroupDefinition } from '../fields/field-groups';
import {
  createFlatMobilePaddingFields,
  resolveFlatMobilePaddingFields,
  fillFlatMobilePadding,
} from '../fields/mobile-padding';
import type { FlatMobilePaddingProps } from '../fields/mobile-padding';
import {
  FIELD_CONTROL_FONT_SIZE,
  FIELD_CONTROL_PADDING,
  FIELD_CONTROL_RADIUS,
  fieldControlCss,
} from '../fields/widgets/field-control-style';
import {
  BODY_LINE_HEIGHT,
  BODY_TEXT_SIZES,
  FONT_ROLES,
  IMAGE_ASPECT_RATIOS,
  IMAGE_SIZES,
  lookup,
  NO_SPACING,
  spacingToCss,
  TEXT_COLOR_ROLES,
} from '../fields/tokens';
import type { ImageAspectRatio, ImageFocalPoint, ImageMobileBehaviour, ImageRadius, ImageSize } from '../fields/tokens';
import { interpolateFromPuck } from '../interpolation';
import { buildImgixDelivery, isImgixHostedSrc } from '../image-src';
import { getCmsStoreImages, resolveImageSource, storeImageRoleLabel } from '../store-images';
import type { ImageSource } from '../store-images';
import { buildCustomImageWidthStoreWrite } from './custom-image-width-store';
import {
  DEFAULT_CUSTOM_IMAGE_WIDTH_PX,
  MAX_FIXED_WIDTH_PX,
  MIN_FIXED_WIDTH_PX,
  imgixSizing,
  resolveCustomImageWidth,
  resolveImageStyle,
} from './image-style';

/**
 * Returns false for src values whose scheme browsers could interpret dangerously
 * (javascript:, vbscript:, data: non-image), true for everything else.
 *
 * `sanitizeUrl` cannot be used here because it rewrites `data:image/png;base64,...` (a legitimate
 * base64 image URI) to `about:blank`, breaking the renderer's byte-identical passthrough guarantee
 * for that shape. This narrower check blocks only the actually-dangerous schemes while leaving
 * every legitimate shape untouched: relative paths, protocol-relative URLs, https://, http://,
 * and data:image/* all pass through unchanged.
 *
 * Leading ASCII whitespace is stripped and the result is lowercased before scheme detection,
 * mirroring how browsers normalise URLs before parsing the scheme (WHATWG URL spec §5.1).
 */
/**
 * Exported so a second image-rendering block cannot end up with a second, subtly different copy of
 * this check. Shared with `slideshow.tsx` and `split-banner.tsx`.
 */
export function isSafeSrc(src: string): boolean {
  // Strip leading ASCII whitespace (tab, LF, FF, CR, space) and lowercase — matches the
  // browser's own pre-parse normalisation step.
  const normalized = src.replace(/^[\t\n\f\r ]+/, '').toLowerCase();
  // Block script-execution schemes via regex — using a regex rather than a string constant
  // avoids the no-script-url lint rule, which scans string literals for those scheme prefixes.
  if (/^(javascript|vbscript):/.test(normalized)) return false;
  // Block data: URIs that are not image types (e.g. data:text/html, data:application/javascript).
  // data:image/* (png, gif, jpeg, webp, svg+xml, ...) are legitimate and must pass through.
  if (normalized.startsWith('data:') && !normalized.startsWith('data:image/')) return false;
  return true;
}

export type AltText = AltTextValue;

const OBJECT_FIT_OPTIONS = [
  { label: 'Fill the frame', value: 'cover' },
  { label: 'Fit inside', value: 'contain' },
] as const;

const SIZE_OPTIONS: { label: string; value: ImageSize }[] = [
  { label: 'Full width', value: 'full' },
  { label: 'Fit content', value: 'fit' },
  { label: 'Small', value: 'small' },
  { label: 'Medium', value: 'medium' },
  { label: 'Large', value: 'large' },
  { label: 'Custom', value: 'custom' },
];

const ASPECT_RATIO_OPTIONS: { label: string; value: ImageAspectRatio }[] = [
  { label: 'Landscape (4:3)', value: 'landscape' },
  { label: 'Original', value: 'original' },
  { label: 'Square (1:1)', value: 'square' },
  { label: 'Wide (16:9)', value: 'wide' },
  { label: 'Portrait (3:4)', value: 'portrait' },
];

// Labels disclose shape hazards: Circle forces a square aspect ratio (see the isCircle coupling
// in resolveImageStyle); Pill and Circle render identically on square images because both radii
// clamp to a full semicircle on the shorter side.
const RADIUS_OPTIONS: { label: string; value: ImageRadius }[] = [
  { label: 'Slightly rounded', value: 'subtle' },
  { label: 'None', value: 'none' },
  { label: 'Rounded', value: 'rounded' },
  { label: 'Circle', value: 'circle' },
  { label: 'Pill', value: 'pill' },
];

// The Image block's "On mobile" control, matching FlexLayout's `mobileStack` naming/shape
// convention (see `tokens.ts`'s `IMAGE_MOBILE_BEHAVIOURS` doc comment).
const MOBILE_OPTIONS: { label: string; value: ImageMobileBehaviour }[] = [
  { label: 'Fill width', value: 'full' },
  { label: 'Keep width', value: 'keep' },
  { label: 'Hide image', value: 'hide' },
];

// Caption alignment: matches link.tsx's `align` control (same value set, same `radio` field type).
// Text now owns alignment inside richtext. `radio` fields need a manual `ai.schema`; `selectField()` only bakes
// schema for `custom` fields.
const CAPTION_ALIGN_OPTIONS: { label: string; value: CaptionAlign }[] = [
  { label: 'Left', value: 'left' },
  { label: 'Center', value: 'center' },
  { label: 'Right', value: 'right' },
];

type CaptionAlign = 'center' | 'left' | 'right';
type ImageAlign = 'center' | 'left' | 'right';

// Validated via lookup() so an unrecognised value falls back to 'left' rather than reaching the
// DOM as an arbitrary CSS custom property value.
const CAPTION_ALIGNS: Record<string, CaptionAlign> = {
  left: 'left',
  center: 'center',
  right: 'right',
};
const IMAGE_ALIGNS: Record<string, ImageAlign> = {
  left: 'left',
  center: 'center',
  right: 'right',
};
const IMAGE_ALIGN_MARGINS: Record<ImageAlign, string> = {
  left: '0 auto 0 0',
  center: '0 auto',
  right: '0 0 0 auto',
};

const CustomWidthRow = styled.div`
  align-items: center;
  display: flex;
  gap: var(--dcm-cms-image-width-field-gap, 4px);
`;

const CustomWidthInput = styled.input`
  ${fieldControlCss}
  border-color: var(
    --dcm-cms-image-width-field-border-color,
    var(--puck-field-color-border, var(--puck-color-border, #d0d4d8))
  );
  border-radius: var(--dcm-cms-image-width-field-radius, ${FIELD_CONTROL_RADIUS});
  flex: 1;
  font-size: var(--dcm-cms-image-width-field-font-size, ${FIELD_CONTROL_FONT_SIZE});
  min-width: 0;
  padding: var(--dcm-cms-image-width-field-inset, ${FIELD_CONTROL_PADDING});

  &:focus-visible {
    outline: 2px solid var(--dcm-cms-image-width-field-focus-color, var(--puck-color-focus-ring, #0b99e6));
    outline-offset: 2px;
  }
`;

const CustomWidthUnit = styled.span`
  align-items: center;
  align-self: stretch;
  background: var(--dcm-cms-image-width-field-unit-bg, #f0f2f4);
  border-radius: var(--dcm-cms-image-width-field-unit-radius, ${FIELD_CONTROL_RADIUS});
  color: var(--dcm-cms-image-width-field-unit-color, #646d72);
  display: inline-flex;
  font-size: var(--dcm-cms-image-width-field-unit-font-size, 12px);
  font-weight: var(--dcm-cms-image-width-field-unit-font-weight, 500);
  justify-content: center;
  padding: var(--dcm-cms-image-width-field-unit-inset, 0 12px);
`;

type CustomImageWidthFieldProps = {
  field: { label?: string };
  name: string;
  value?: unknown;
  onChange: (value: number) => void;
  readOnly?: boolean;
};

function CustomImageWidthField({ field, name, value, onChange, readOnly }: CustomImageWidthFieldProps): JSX.Element {
  const helpId = React.useId();
  const resolvedValue = resolveCustomImageWidth(value);
  const [draft, setDraft] = React.useState(String(resolvedValue));
  const { selectedItem, dispatch, getSelectorForId } = usePuck();

  React.useEffect(() => setDraft(String(resolvedValue)), [resolvedValue]);

  const commitDraft = React.useCallback(() => {
    const nextValue = draft === '' ? DEFAULT_CUSTOM_IMAGE_WIDTH_PX : resolveCustomImageWidth(Number(draft));
    setDraft(String(nextValue));

    const selector = selectedItem ? getSelectorForId(selectedItem.props.id) : undefined;
    const write = buildCustomImageWidthStoreWrite({ selectedItem, selector, nextValue });
    if (write.kind === 'fallback') {
      onChange(nextValue);
      return;
    }
    if (write.kind === 'dispatch' && write.action) {
      dispatch(write.action as Parameters<typeof dispatch>[0]);
    }
  }, [dispatch, draft, getSelectorForId, onChange, selectedItem]);

  const handleKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') event.currentTarget.blur();
  }, []);

  return (
    <FieldLabelWithHelp
      label={field.label ?? name}
      readOnly={readOnly}
      helpId={helpId}
      helpStyle={{
        color: 'var(--dcm-cms-image-width-field-help-color, var(--puck-color-text-secondary, #646d72))',
        fontSize: 'var(--dcm-cms-image-width-field-help-font-size, 12px)',
        lineHeight: 'var(--dcm-cms-image-width-field-help-line-height, 1.4)',
        margin: 'var(--dcm-cms-image-width-field-help-margin, 0)',
      }}
      help='Keep width preserves this value on phones. Fill width overrides it.'
    >
      <CustomWidthRow>
        <CustomWidthInput
          aria-describedby={helpId}
          aria-label={`${field.label ?? name} (pixels)`}
          data-testid={`number-input-${name}`}
          disabled={readOnly}
          max={MAX_FIXED_WIDTH_PX}
          min={MIN_FIXED_WIDTH_PX}
          onBlur={commitDraft}
          onChange={(event) => setDraft(event.currentTarget.value)}
          onKeyDown={handleKeyDown}
          step={1}
          type='number'
          value={draft}
        />
        <CustomWidthUnit aria-hidden='true' data-testid={`number-unit-${name}`}>
          px
        </CustomWidthUnit>
      </CustomWidthRow>
    </FieldLabelWithHelp>
  );
}

export type ImageProps = FlatMobilePaddingProps &
  FlatSpacingProps<'padding'> & {
    source: ImageSource;
    alt: AltText;
    caption: string;
    borderRadius: string;
    width: string;
    customWidth: number;
    imageAlign: ImageAlign;
    mobileDisplay: ImageMobileBehaviour;
    aspectRatio: ImageAspectRatio;
    objectFit: string;
    focalPoint: ImageFocalPoint;
    captionAlign: CaptionAlign;
    linkHref: string;
    linkOpenInNewTab: boolean;
  };

/** Placeholder shown in the editor before an image source has been set or resolved. */
export function ImagePlaceholder({
  width = '100%',
  maxWidth,
  aspectRatio = IMAGE_ASPECT_RATIOS.original,
  borderRadius,
  margin = IMAGE_ALIGN_MARGINS.left,
  message = 'No image source set',
}: {
  width?: string;
  maxWidth?: string;
  aspectRatio?: string;
  borderRadius?: string;
  margin?: string;
  message?: string;
}): JSX.Element {
  // 'auto' (the Original preset) has no intrinsic size to fall back on before an image URL is
  // set, so give the empty state an explicit height in that case; any real ratio can size itself.
  const isAutoRatio = aspectRatio === 'auto';

  return (
    <div
      style={{
        width,
        maxWidth,
        margin,
        aspectRatio: isAutoRatio ? undefined : aspectRatio,
        height: isAutoRatio ? 200 : undefined,
        background: 'var(--dcm-cms-image-placeholder-bg, #f0f2f4)',
        borderRadius,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--dcm-cms-image-placeholder-color, #646d72)',
        fontSize: 14,
      }}
    >
      {message}
    </div>
  );
}

/**
 * Mobile visibility rule for 'hide'. Applied to {@link StyledImage} when there is no caption, or
 * to {@link StyledFigure} when there is one (hides image and caption together with one rule).
 * Set to `display: none` in production only -- left unset in the editor so the block stays
 * visible/selectable; see render's `isEditing` branch.
 *
 * This is mobile *visibility*; mobile *width* ('full'/'keep', `--cms-image-mobile-width`) is a
 * separate concern that only ever applies to {@link StyledImage}.
 */
const mobileHideOnHideRule = css`
  @media ${mediaQueries.largePhone} {
    display: var(--cms-image-mobile-display, block);
  }
`;

const StyledImage = styled.img`
  display: block;
  width: var(--cms-image-width, 100%);
  max-width: var(--cms-image-max-width, none);
  margin: var(--cms-image-margin, 0 auto 0 0);
  aspect-ratio: var(--cms-image-aspect-ratio, auto);
  object-fit: var(--cms-image-object-fit, cover);
  object-position: var(--cms-image-focal-point, center center);
  border-radius: var(--cms-image-border-radius, 0);

  @media ${mediaQueries.largePhone} {
    /* Falls back to the desktop width when 'keep' -- see spacer.tsx's mobileHeight for the same
       fallback-chain trick, so 'keep' has no second value to keep in sync. Mobile WIDTH is only
       ever set here, never on StyledFigure -- it governs the image's own box, not the block's
       visibility (see mobileHideOnHideRule above for that separate concern). */
    width: var(--cms-image-mobile-width, var(--cms-image-width, 100%));
  }

  ${mobileHideOnHideRule}
`;

/**
 * Outermost wrapper for the rendered block whenever there's a caption to hide alongside the
 * image -- see `mobileHideOnHideRule` above for why hiding THIS element (rather than duplicating
 * the same rule onto {@link StyledImage} and {@link StyledCaption} individually) is what makes
 * 'Hide on mobile' actually drop the whole block, caption included, instead of leaving a dangling
 * caption with no image above it on phones.
 */
const StyledFigure = styled.figure`
  margin: 0;

  ${mobileHideOnHideRule}
`;

/** Caption typography matches paragraph.tsx's theme-token approach: no per-instance override
 * controls are offered for it, so the tokens are the only fallback layer (no `--cms-image-*`
 * override property needed, since there is nothing an author can set to override it). */
const StyledCaption = styled.figcaption`
  overflow-wrap: anywhere;
  margin: var(--dcm-cms-image-caption-margin, 8px 0 0);
  color: var(--dcm-cms-image-caption-color, ${TEXT_COLOR_ROLES.muted});
  font-family: var(--dcm-cms-image-caption-font-family, ${FONT_ROLES.body});
  font-size: var(--dcm-cms-image-caption-font-size, ${BODY_TEXT_SIZES.sm});
  line-height: var(--dcm-cms-image-caption-line-height, ${BODY_LINE_HEIGHT});
  /* Per-instance override, unlike the theme tokens above -- captionAlign is an author choice,
     not a merchant theme setting, so it goes through a --cms-* (not --dcm-*) property. */
  text-align: var(--cms-image-caption-align, left);
`;

type ImageLinkProps = {
  href: string;
  openInNewTab: boolean;
  isEditing: boolean;
  children: React.ReactNode;
  /** Accessible name for the link when the image inside it contributes no accessible name (e.g.
   * decorative image or empty alt). Only applied to the production `<a>`; the editor `<span>`
   * branch is left alone -- Puck's own selection/drag UI conveys identity there. */
  ariaLabel?: string;
};

/**
 * Wraps the image in a real `<a>` in production, following `link.tsx`'s URL conventions
 * (`sanitizeUrl`, `rel="noopener noreferrer"` only when opening in a new tab).
 *
 * In the Puck editor, an `<a>` around a draggable/selectable block swallows Puck's own
 * drag/selection pointer events (cms/AGENTS.md gotcha #3). A non-interactive `<span>` stands in
 * for the link in the editor; the real `<a>` takes over in production.
 *
 * Renders no wrapper at all, in either mode, when there is no link.
 */
function ImageLink({ href, openInNewTab, isEditing, children, ariaLabel }: ImageLinkProps): JSX.Element {
  if (!href) return <>{children}</>;

  if (isEditing) {
    // Editor: plain, non-interactive element so Puck's own drag/selection handling on the image
    // underneath is not swallowed by a nested interactive <a>.
    return <span style={{ display: 'block' }}>{children}</span>;
  }

  return (
    <a
      href={sanitizeUrl(href)}
      target={openInNewTab ? '_blank' : undefined}
      // Without noopener the destination gets a window.opener handle back into the storefront.
      rel={openInNewTab ? 'noopener noreferrer' : undefined}
      style={{ display: 'block' }}
      aria-label={ariaLabel}
    >
      {children}
    </a>
  );
}

export const ImagePadding = styled.div`
  box-sizing: border-box;
  width: var(--cms-image-width, 100%);
  max-width: 100%;
  margin: var(--cms-image-margin, 0 auto 0 0);
  padding: var(--cms-image-padding, 0px);

  @media ${mediaQueries.largePhone} {
    width: var(--cms-image-mobile-width, var(--cms-image-width, 100%));
    padding: var(--cms-image-mobile-padding, var(--cms-image-padding, 0px));
  }

  ${mobileHideOnHideRule}
`;

const IMAGE_FIELD_GROUPS: readonly FieldGroupDefinition<keyof ImageProps>[] = [
  { label: 'Appearance', fields: ['borderRadius'] },
  {
    label: 'Layout',
    fields: ['width', 'customWidth', 'aspectRatio', 'objectFit', 'focalPoint', 'imageAlign', 'captionAlign'],
  },
  { label: 'Mobile', fields: ['mobileDisplay'] },
  { label: 'Link', fields: ['linkHref', 'linkOpenInNewTab'] },
  { label: 'Accessibility', fields: ['alt'] },
  {
    label: 'Spacing',
    fields: [
      { label: 'Padding', variant: 'card', fields: ['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft'] },
      'differentMobilePadding',
      {
        label: 'Mobile padding',
        variant: 'card',
        fields: ['mobilePaddingTop', 'mobilePaddingRight', 'mobilePaddingBottom', 'mobilePaddingLeft'],
      },
    ],
  },
];

/** Image block: a URL plus link, sizing, cropping, and captioning controls. */
export const imageConfig: ComponentConfig<ImageProps> = {
  label: 'Image',
  ai: {
    instructions:
      'Displays an image from a URL. Use for hero images, illustrations, and media. Always provide alt text for accessibility, or mark the image decorative if it is purely visual.',
  },
  fields: {
    source: {
      type: 'custom',
      label: 'Image',
      render: ImageSourceField,
      ai: { exclude: true },
    },
    caption: {
      type: 'text',
      label: 'Caption',
      labelIcon: <span aria-hidden='true' data-cms-image-caption-field='true' />,
      placeholder: 'Add a caption displayed below the image',
    },
    alt: altTextField(
      'Alt text override',
      'Descriptive alt text for screen readers. Mark the image decorative instead if it conveys no information of its own (e.g. a background flourish).'
    ),
    borderRadius: selectField(
      'Corner rounding',
      RADIUS_OPTIONS,
      "Corner treatment. 'Pill' and 'Circle' are shape presets relative to the image's own box, not a themed radius -- 'Circle' also forces a square aspect ratio so it renders as an actual circle. On a square image, 'Pill' and 'Circle' render identically. 'Slightly rounded' and 'Rounded' use this block's own image-scaled radius tokens.",
      "On a square image, 'Pill' and 'Circle' look identical - both round every corner into a full half-circle."
    ),
    width: selectField(
      'Width',
      SIZE_OPTIONS,
      "Image width. 'Full width' fills the column; 'Fit content' shrinks to the image's own size. 'Small'/'Medium'/'Large' use the Image block's own size scale; 'Custom' uses the pixel Width field."
    ),
    customWidth: {
      type: 'custom',
      label: 'Width',
      render: CustomImageWidthField,
      ai: {
        schema: {
          type: 'integer',
          minimum: MIN_FIXED_WIDTH_PX,
          maximum: MAX_FIXED_WIDTH_PX,
          multipleOf: 1,
        },
        instructions: `Custom image width in whole pixels from ${MIN_FIXED_WIDTH_PX} to ${MAX_FIXED_WIDTH_PX}.`,
      },
    },
    aspectRatio: selectField(
      'Shape',
      ASPECT_RATIO_OPTIONS,
      "'Original' uses the image's own proportions, uncropped. The other presets impose a fixed shape, cropping according to the Image fit and Focal point controls below. Corner rounding's 'Circle' option forces a square and hides this control."
    ),
    objectFit: {
      type: 'radio',
      label: 'Image fit',
      options: [...OBJECT_FIT_OPTIONS],
      ai: {
        schema: { type: 'string', enum: OBJECT_FIT_OPTIONS.map(({ value }) => value) },
        instructions:
          "Only shown for a fixed Shape. 'Fill the frame' crops the image to cover the shape completely; 'Fit inside' shrinks the image so all of it stays visible, which can leave empty space.",
      },
    },
    focalPoint: focalPointField(
      'Focal point',
      'Which part of the image to keep visible when cropping. Only meaningful when Image fit is set to Fill the frame.',
      "Only has an effect when Image fit is set to 'Fill the frame'."
    ),
    imageAlign: {
      type: 'radio',
      label: 'Image alignment',
      options: [...CAPTION_ALIGN_OPTIONS],
      ai: { schema: { type: 'string', enum: ['left', 'center', 'right'] } },
    },
    captionAlign: {
      type: 'radio',
      label: 'Caption alignment',
      options: [...CAPTION_ALIGN_OPTIONS],
      ai: { schema: { type: 'string', enum: ['left', 'center', 'right'] } },
    },
    mobileDisplay: {
      type: 'radio',
      label: 'Mobile',
      options: [...MOBILE_OPTIONS],
      ai: {
        schema: { type: 'string', enum: MOBILE_OPTIONS.map(({ value }) => value) },
        instructions:
          "'Fill width' makes the image fill its column on phones regardless of the chosen Width. 'Keep width' preserves the chosen preset or custom width, with safe shrinking if the phone is narrower. 'Hide image' removes the whole block -- image and caption -- on phones; it stays visible, de-emphasised, in the editor so it remains selectable.",
      },
    },
    linkHref: destinationField('Destination', {
      instructions: 'Internal storefront page or external URL for the whole image. Leave empty for no destination.',
    }),
    linkOpenInNewTab: toggleField('Open in new tab'),
    ...createFlatSpacingFields('padding', { max: 96 }),
    ...createFlatMobilePaddingFields({ max: 96 }),
  },
  renderFields: ({ fields }) => (
    <>
      {fields.source}
      {fields.caption}
      {renderFieldGroups(fields, IMAGE_FIELD_GROUPS)}
    </>
  ),
  resolveData: ({ props }) => ({ props: fillFlatMobilePadding(props, NO_SPACING) }),
  resolveFields: (data, { fields }) => {
    const { width, objectFit, aspectRatio, caption, borderRadius, linkHref } = data.props;
    const hasCroppingFrame =
      ASPECT_RATIO_OPTIONS.some(({ value }) => value !== 'original' && value === aspectRatio) ||
      borderRadius === 'circle';
    // Puck displays keys in insertion order; remove hidden fields without moving their siblings.
    const visible: Partial<Fields<ImageProps>> = {
      ...resolveFlatMobilePaddingFields(data.props.differentMobilePadding, fields),
    };
    if (width !== 'custom') delete visible.customWidth;
    if (borderRadius === 'circle') delete visible.aspectRatio;
    if (!hasCroppingFrame) delete visible.objectFit;
    if (((objectFit as unknown) ?? 'cover') !== 'cover' || !hasCroppingFrame) delete visible.focalPoint;
    if (typeof width !== 'string' || width === '' || width === 'full' || width === IMAGE_SIZES.full) {
      delete visible.imageAlign;
    }
    if (typeof caption !== 'string' || caption.trim() === '') delete visible.captionAlign;
    if (typeof linkHref !== 'string' || linkHref.trim() === '') delete visible.linkOpenInNewTab;
    return visible as Fields<ImageProps>;
  },
  defaultProps: {
    source: { kind: 'url', src: '' },
    alt: { alt: '', decorative: false },
    caption: '',
    borderRadius: 'none',
    width: 'full',
    customWidth: DEFAULT_CUSTOM_IMAGE_WIDTH_PX,
    imageAlign: 'left',
    mobileDisplay: 'full',
    aspectRatio: 'original',
    objectFit: 'cover',
    focalPoint: 'center',
    captionAlign: 'left',
    linkHref: '',
    linkOpenInNewTab: false,
    paddingTop: '0px',
    paddingRight: '0px',
    paddingBottom: '0px',
    paddingLeft: '0px',
    differentMobilePadding: false,
    mobilePaddingTop: '0px',
    mobilePaddingRight: '0px',
    mobilePaddingBottom: '0px',
    mobilePaddingLeft: '0px',
  },
  render: (rawProps) => {
    const { puck, differentMobilePadding = false } = rawProps;

    const asString = (value: unknown, fallback = ''): string => {
      if (value === undefined) return fallback;
      return typeof value === 'string' ? value : '';
    };

    // Published JSON is unvalidated; normalize each flat value independently.
    const imageSource = resolveImageSource(rawProps.source, getCmsStoreImages(puck));
    const { src } = imageSource;
    const resolvedAlt = resolveAltText(rawProps.alt);
    const caption = asString(rawProps.caption);
    const borderRadius = asString(rawProps.borderRadius, 'none');
    const width = asString(rawProps.width, 'full');
    const { customWidth } = rawProps;
    const imageAlign = lookup<ImageAlign>(IMAGE_ALIGNS, asString(rawProps.imageAlign, 'left'), 'left');
    const aspectRatio = asString(rawProps.aspectRatio, 'original');
    const objectFit = asString(rawProps.objectFit, 'cover');
    const focalPoint = asString(rawProps.focalPoint, 'center');
    const captionAlign = lookup<CaptionAlign>(CAPTION_ALIGNS, asString(rawProps.captionAlign, 'left'), 'left');
    const mobile = asString(rawProps.mobileDisplay, 'full');
    const linkHref = asString(rawProps.linkHref);
    const linkOpenInNewTab = (rawProps.linkOpenInNewTab as unknown) === true;
    // Trim at the single point the alt attribute is produced; whitespace-only alt is effectively empty.
    const altAttribute = resolvedAlt.decorative ? '' : resolvedAlt.alt.trim();

    // Computed unconditionally above the early `!src` return so it can gate that return.
    // `puck` is typed as always-present by @puckeditor/core, but some test harnesses render
    // this component's `render` directly without supplying it -- guard defensively.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const isEditing = Boolean(puck?.isEditing);

    const resolved = resolveImageStyle({
      width,
      customWidth,
      aspectRatio,
      objectFit,
      focalPoint,
      borderRadius,
      mobile,
    });

    const padding = spacingToCss(readBoxSpacing(rawProps, 'padding'), 96);
    const isMobilePaddingActive = (differentMobilePadding as unknown) === true;
    const mobilePaddingCss = isMobilePaddingActive
      ? spacingToCss(readBoxSpacing(fillFlatMobilePadding(rawProps, NO_SPACING), 'mobilePadding'), 96)
      : padding;
    const hasPadding =
      padding !== '0px 0px 0px 0px' || (isMobilePaddingActive && mobilePaddingCss !== '0px 0px 0px 0px');
    const mediaWidth = hasPadding ? '100%' : resolved.width;
    // Zero spacing keeps the media itself as the Flex/Grid item. With spacing, the frame owns
    // the authored width and the media fills its content box, so percentage widths apply once.
    const withPadding = (children: React.ReactNode): JSX.Element => {
      if (!hasPadding) return <>{children}</>;
      return (
        <ImagePadding
          data-testid='cms-image-padding'
          style={
            {
              '--cms-image-padding': padding,
              // Always set both variables so nested Images cannot inherit a parent's mobile override.
              '--cms-image-mobile-padding': mobilePaddingCss,
              '--cms-image-width': resolved.width,
              '--cms-image-margin': IMAGE_ALIGN_MARGINS[imageAlign],
              ...(resolved.mobileWidth ? { '--cms-image-mobile-width': resolved.mobileWidth } : {}),
              ...(resolved.mobileHide && !isEditing ? { '--cms-image-mobile-display': 'none' } : {}),
            } as React.CSSProperties
          }
        >
          {children}
        </ImagePadding>
      );
    };

    if (!src || !isSafeSrc(src)) {
      // Only show authoring guidance in the editor; production emits nothing for missing, malformed,
      // unavailable dynamic, or unsafe sources.
      if (!isEditing) return <></>;

      const selectedRole = imageSource.source?.kind === 'store-image' ? imageSource.source.role : undefined;
      const missingRoleMessage =
        selectedRole && imageSource.missingStoreRole
          ? `${storeImageRoleLabel(
              selectedRole
            )} is not configured for the current Previewing store. Choose another image or configure this role in Store settings.`
          : undefined;

      return withPadding(
        <ImagePlaceholder
          // An empty width means "unset", so fall back to the placeholder's own default.
          width={mediaWidth === '' ? undefined : mediaWidth}
          maxWidth={width === 'custom' ? '100%' : undefined}
          margin={hasPadding ? '0' : IMAGE_ALIGN_MARGINS[imageAlign]}
          aspectRatio={resolved.aspectRatio}
          borderRadius={resolved.borderRadius}
          message={missingRoleMessage}
        />
      );
    }

    const resolvedCaption = interpolateFromPuck(caption, puck);
    const hasCaption = resolvedCaption.trim() !== '';
    const effectiveLinkHref = linkHref.trim() === '' ? '' : linkHref;

    // 'hide' sets display:none at the mobile breakpoint in production only. In the editor the
    // block stays visible/selectable; reduced opacity is applied instead (below). WHERE this var
    // lands -- on the image or the figure -- depends on whether there is a caption to hide with it.
    const hideOnMobileStyle: React.CSSProperties =
      resolved.mobileHide && !isEditing ? ({ '--cms-image-mobile-display': 'none' } as React.CSSProperties) : {};

    const imageStyle = {
      '--cms-image-width': mediaWidth,
      ...(width === 'custom' ? { '--cms-image-max-width': '100%' } : {}),
      '--cms-image-margin': hasPadding ? '0' : IMAGE_ALIGN_MARGINS[imageAlign],
      '--cms-image-aspect-ratio': resolved.aspectRatio,
      '--cms-image-object-fit': resolved.objectFit,
      '--cms-image-focal-point': resolved.focalPoint,
      '--cms-image-border-radius': resolved.borderRadius,
      // Without padding, 'keep' leaves mobile width unset to inherit the desktop width.
      // With padding, the frame owns responsive sizing and the image fills its content box.
      '--cms-image-mobile-width': hasPadding ? '100%' : resolved.mobileWidth,
      // The resolved caption matches the figure branch below, so a token that resolves empty is
      // treated consistently as no caption and the mobile hide variable lands on the image.
      ...(hasCaption ? {} : hideOnMobileStyle),
    } as React.CSSProperties;

    // Imgix-hosted sources are delivered with optimised src/srcSet/sizes. Non-Imgix sources
    // (relative paths, data: URIs, third-party URLs, raw S3) pass through untouched.
    const imgixDelivery = isImgixHostedSrc(src) ? buildImgixDelivery(src, imgixSizing(width, customWidth)) : undefined;

    const imgAttributes: React.ImgHTMLAttributes<HTMLImageElement> = {
      src: imgixDelivery?.src ?? src,
      ...(imgixDelivery
        ? { srcSet: imgixDelivery.srcSet, ...(imgixDelivery.sizes !== undefined ? { sizes: imgixDelivery.sizes } : {}) }
        : {}),
      alt: altAttribute,
      loading: 'lazy',
      style: {
        ...imageStyle,
        // Editor-only de-emphasis for 'hide' -- see the CSS var comment above for why production
        // uses `display: none` at the breakpoint instead of this.
        ...(resolved.mobileHide && isEditing ? { opacity: 0.4 } : {}),
      },
    };

    const image = <StyledImage {...imgAttributes} />;

    // WCAG 2.4.4 / 4.1.2: a link must have an accessible name. When the image contributes none
    // (decorative or empty/whitespace alt), use the trimmed caption as the fallback. The URL is
    // deliberately never used -- a raw href is not an accessible name. When altAttribute is
    // non-empty the image already names the link naturally; the isEditing branch emits a <span>
    // so no aria-label is needed there.
    function getLinkAriaLabel(): string | undefined {
      if (!effectiveLinkHref || altAttribute !== '') return undefined;
      const trimmedCaption = resolvedCaption.trim();
      return trimmedCaption !== '' ? trimmedCaption : undefined;
    }
    const linkAriaLabel = getLinkAriaLabel();

    // Editor-only warning when a linked image has no accessible name (no alt and no caption).
    // Only fires when decorative/empty-alt meets a live link; a decorative image with no link is
    // a valid authoring choice and must not warn.
    const showLinkedEmptyAltWarning = isEditing && Boolean(effectiveLinkHref) && altAttribute === '' && !hasCaption;

    const linkedImage = (
      <ImageLink
        href={effectiveLinkHref}
        openInNewTab={linkOpenInNewTab}
        isEditing={isEditing}
        ariaLabel={linkAriaLabel}
      >
        {image}
      </ImageLink>
    );

    // A caption that is empty after interpolation produces no aria-label, so it must also produce
    // no figcaption.
    if (!hasCaption) {
      if (!showLinkedEmptyAltWarning) return withPadding(linkedImage);
      // Editor-only fragment: the warning renders below the linked image without affecting
      // production layout. `showLinkedEmptyAltWarning` is always false in production.
      return withPadding(
        <>
          {linkedImage}
          <p data-testid='cms-image-linked-empty-alt-warning' style={{ marginTop: 6, fontSize: 12, color: '#a15c00' }}>
            This image links somewhere but has no alt text. Screen reader users will hear an unnamed link. Add alt text
            describing the link destination, or add a caption (it becomes the link&apos;s accessible name).
          </p>
        </>
      );
    }

    // A decorative image is invisible to the accessibility tree, so <figure>/<figcaption>
    // would caption nothing the user can perceive -- use <div>/<p> via styled-components' `as`.
    const wrapperTag = resolvedAlt.decorative ? 'div' : 'figure';
    const captionTag = resolvedAlt.decorative ? 'p' : 'figcaption';

    return withPadding(
      // The hide-on-mobile var goes on the figure so it hides image and caption together.
      <StyledFigure as={wrapperTag} style={hideOnMobileStyle}>
        {linkedImage}
        <StyledCaption as={captionTag} style={{ '--cms-image-caption-align': captionAlign } as React.CSSProperties}>
          {resolvedCaption}
        </StyledCaption>
      </StyledFigure>
    );
  },
};
